
import Vue from "vue";
import Component from "vue-class-component";
import mapboxgl, { Map, Marker } from "mapbox-gl";
import { traccarService, TraccarPosition } from "@/services/TraccarService";

const env = process.env;
mapboxgl.accessToken = `${env.VUE_APP_MAP_BOX}`;

@Component
export default class VehicleTrackingIndex extends Vue {
  vehicleId: string = '';
  period: string = '';
  map?: Map;
  vehicleMarker?: Marker;
  traccarUniqueId: string = env.VUE_APP_TRACCAR_ID || '';
  loading: boolean = false;
  errorMessage: string = '';
  
  // Replay Route properties
  showReplayOptions: boolean = false;
  replayPeriod: string = '';
  replayLoading: boolean = false;
  replayErrorMessage: string = '';
  customFromDate: string = '';
  customToDate: string = '';
  
  // View Reports properties
  showReportOptions: boolean = false;
  reportPeriod: string = '';
  reportLoading: boolean = false;
  reportErrorMessage: string = '';
  reportCustomFromDate: string = '';
  reportCustomToDate: string = '';
  reportData: any = null;
  reportGeneratedPeriod: string = ''; // Store the period used for the current report
  
  // Route visualization properties
  routeMarkers: Marker[] = [];
  routeLineLayerId: string = 'route-line';
  animationId: number | null = null;
  isAnimating: boolean = false;
  currentPositions: TraccarPosition[] = [];
  
  // Current vehicle data
  currentVehicleData: any = null;

  mounted() {
    this.initMap();
    this.loadVehiclePosition();
  }

  beforeDestroy() {
    this.destroyMap();
  }

  async searchVehicle() {
    // console.log('Searching for vehicle:', this.vehicleId, 'Period:', this.period);
    this.loading = true;
    this.errorMessage = '';
    
    try {
      await this.loadVehiclePosition();
    } catch (error) {
      console.error('Error searching vehicle:', error);
      this.errorMessage = 'Failed to load vehicle data. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  toggleReplayRoute() {
    this.showReplayOptions = !this.showReplayOptions;
    this.replayErrorMessage = '';
    if (!this.showReplayOptions) {
      this.replayPeriod = '';
      this.customFromDate = '';
      this.customToDate = '';
    }
    // Close reports if open
    if (this.showReplayOptions) {
      this.showReportOptions = false;
    }
  }

  toggleViewReports() {
    this.showReportOptions = !this.showReportOptions;
    this.reportErrorMessage = '';
    if (!this.showReportOptions) {
      this.reportPeriod = '';
      this.reportCustomFromDate = '';
      this.reportCustomToDate = '';
    }
    // Close replay if open
    if (this.showReportOptions) {
      this.showReplayOptions = false;
    }
  }

  async startReplayRoute() {
    try {
      this.replayLoading = true;
      this.replayErrorMessage = '';
      
      const { from, to } = this.getDateRange();
      // console.log('Replay route from:', from, 'to:', to);
      
      // Get the device first to get the device ID
      const devices = await traccarService.getDeviceByUniqueId(this.traccarUniqueId);
      if (devices.length === 0) {
        throw new Error('Device not found');
      }
      
      const deviceId = devices[0].id;
      // console.log('Device ID for replay:', deviceId);
      
      // Get positions for the specified period
      const positions = await traccarService.getPositions(deviceId, from, to);
      // console.log('Replay positions:', positions);
      
      if (positions.length === 0) {
        this.replayErrorMessage = 'No route data found for the selected period';
        return;
      }
      
      // Display route on map
      this.displayRouteOnMap(positions);
      // console.log(`Displayed ${positions.length} positions on map`);
      
      this.showReplayOptions = false;
    } catch (error) {
      console.error('Error loading replay route:', error);
      this.replayErrorMessage = 'Failed to load route data. Please try again.';
    } finally {
      this.replayLoading = false;
    }
  }

  async generateReport() {
    try {
      this.reportLoading = true;
      this.reportErrorMessage = '';
      
      const { from, to } = this.getReportDateRange();
      
      // Get the device first to get the device ID
      const devices = await traccarService.getDeviceByUniqueId(this.traccarUniqueId);
      if (devices.length === 0) {
        throw new Error('Device not found');
      }
      
      const device = devices[0];
      const deviceId = device.id;
      
      // Get positions for the specified period
      const positions = await traccarService.getPositions(deviceId, from, to);
      
      if (positions.length === 0) {
        this.reportErrorMessage = 'No data found for the selected period';
        return;
      }
      
      // Calculate report data
      this.reportData = this.calculateReportData(positions, device);
      this.reportGeneratedPeriod = this.reportPeriod; // Store the period used for this report
      this.showReportOptions = false;
      
    } catch (error) {
      console.error('Error generating report:', error);
      this.reportErrorMessage = 'Failed to generate report. Please try again.';
    } finally {
      this.reportLoading = false;
    }
  }

  calculateReportData(positions: TraccarPosition[], device: any) {
    if (positions.length === 0) return null;

    // Sort positions by time
    const sortedPositions = positions.sort((a, b) => 
      new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime()
    );

    // Calculate total distance manually (more accurate than Traccar's totalDistance attribute)
    let totalDistance = 0;
    for (let i = 1; i < sortedPositions.length; i++) {
      const prev = sortedPositions[i - 1];
      const curr = sortedPositions[i];
      const segmentDistance = this.calculateDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
      if (segmentDistance > 0.01) { // Filter GPS noise - only movements > 10 meters
        totalDistance += segmentDistance;
      }
    }
    
    // Calculate speeds (convert from knots to km/h) - back to original method
    const speeds = sortedPositions.map(pos => pos.speed * 1.852);
    const validSpeeds = speeds.filter(speed => speed > 0);
    
    // Calculate average speed from all valid speed readings (original method)
    const averageSpeed = validSpeeds.length > 0 
      ? Math.round(validSpeeds.reduce((sum, speed) => sum + speed, 0) / validSpeeds.length)
      : 0;
    
    // Calculate max speed
    const maxSpeed = Math.round(Math.max(...speeds));
    
    // Calculate overspeed events and distance (speed > 120 km/h)
    let overspeedEvents = 0;
    let overspeedDistance = 0;
    let previousOverspeedPosition = null;
    
    for (let i = 0; i < sortedPositions.length; i++) {
      const pos = sortedPositions[i];
      const currentSpeed = pos.speed * 1.852; // Convert knots to km/h
      
      if (currentSpeed > 120) {
        // Count as an overspeed event if this is the first overspeed or there was a gap
        if (previousOverspeedPosition === null || 
            (new Date(pos.fixTime).getTime() - new Date(previousOverspeedPosition.fixTime).getTime()) > 5 * 60 * 1000) { // 5 min gap
          overspeedEvents++;
        }
        
        // Calculate distance during overspeed
        if (i > 0) {
          const prevPos = sortedPositions[i - 1];
          const prevSpeed = prevPos.speed * 1.852;
          
          // If both current and previous positions are overspeeding, add to overspeed distance
          if (prevSpeed > 120) {
            const segmentDistance = this.calculateDistance(prevPos.latitude, prevPos.longitude, pos.latitude, pos.longitude);
            if (segmentDistance > 0.01) { // Filter GPS noise
              overspeedDistance += segmentDistance;
            }
          }
        }
        
        previousOverspeedPosition = pos;
      } else {
        // Reset if speed drops below 120 km/h
        if (previousOverspeedPosition !== null) {
          previousOverspeedPosition = null;
        }
      }
    }
    
    // Calculate engine hours (time when ignition was on)
    let engineHours = 0;
    
    for (let i = 1; i < sortedPositions.length; i++) {
      const prev = sortedPositions[i - 1];
      const curr = sortedPositions[i];
      
      // If current position has ignition on, count the time since previous position
      if (curr.attributes?.ignition) {
        const timeDiff = new Date(curr.fixTime).getTime() - new Date(prev.fixTime).getTime();
        engineHours += timeDiff / (1000 * 60 * 60); // Convert to hours
      }
    }
    
    engineHours = Math.round(engineHours * 100) / 100;
    
    // Get odometer values (if available in attributes)
    const firstPos = sortedPositions[0];
    const lastPos = sortedPositions[sortedPositions.length - 1];
    const odometerStart = firstPos.attributes?.odometer ? Math.round(firstPos.attributes.odometer / 1000) : 0;
    const odometerEnd = lastPos.attributes?.odometer ? Math.round(lastPos.attributes.odometer / 1000) : 0;

    // Get coolant temperature from most recent position with temp data
    let coolantTemp = null;
    for (let i = sortedPositions.length - 1; i >= 0; i--) {
      const pos = sortedPositions[i];
      if (pos.attributes?.coolantTemp || pos.attributes?.engineCoolantTemperature || pos.attributes?.temp1) {
        coolantTemp = pos.attributes.coolantTemp || pos.attributes.engineCoolantTemperature || pos.attributes.temp1;
        break;
      }
    }

    // Get fuel levels from start and end positions
    const fuelStart = firstPos.attributes?.fuelLevel || firstPos.attributes?.fuel || null;
    const fuelEnd = lastPos.attributes?.fuelLevel || lastPos.attributes?.fuel || null;

    // Calculate engine RPM statistics
    const rpmValues = sortedPositions
      .map(pos => pos.attributes?.engineRpm || pos.attributes?.rpm)
      .filter(rpm => rpm !== undefined && rpm !== null && rpm > 0);
    
    const avgEngineRpm = rpmValues.length > 0 
      ? Math.round(rpmValues.reduce((sum, rpm) => sum + rpm, 0) / rpmValues.length)
      : null;
    
    const maxEngineRpm = rpmValues.length > 0 ? Math.max(...rpmValues) : null;

    // Get VIN from device attributes (VIN is usually stored in device, not positions)
    const vin = device.attributes?.vin || device.attributes?.VIN || device.attributes?.vehicleIdentificationNumber;

    return {
      averageSpeed,
      maxSpeed,
      totalDistance: Math.round(totalDistance * 100) / 100,
      engineHours,
      odometerStart,
      odometerEnd,
      totalPositions: positions.length,
      coolantTemp: coolantTemp ? Math.round(coolantTemp) : null,
      vin: vin || null,
      fuelStart: fuelStart ? Math.round(fuelStart) : null,
      fuelEnd: fuelEnd ? Math.round(fuelEnd) : null,
      avgEngineRpm,
      maxEngineRpm,
      overspeedEvents,
      overspeedDistance: Math.round(overspeedDistance * 100) / 100
    };
  }

  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  formatReportPeriod(): string {
    const periodName = this.getReportPeriodName(this.reportGeneratedPeriod);
    const dateRange = this.getFormattedDateRange(this.reportGeneratedPeriod);
    return `${periodName} (${dateRange})`;
  }

  getReportPeriodName(period: string): string {
    switch (period) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'week': return 'This Week';
      case 'lastWeek': return 'Last Week';
      case 'month': return 'This Month';
      case 'custom': return 'Custom Period';
      default: return '';
    }
  }

  getFormattedDateRange(period: string): string {
    try {
      const { from, to } = this.getReportDateRangeForPeriod(period);
      const fromDate = new Date(from);
      const toDate = new Date(to);
      
      const formatDateTime = (date: Date) => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
        
        const month = months[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        
        return `${month} ${day}, ${year} ${hours}:${minutes}:${seconds}`;
      };
      
      return `${formatDateTime(fromDate)} to ${formatDateTime(toDate)}`;
    } catch (error) {
      return 'Invalid date range';
    }
  }

  getReportDateRangeForPeriod(period: string): { from: string; to: string } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (period) {
      case 'today':
        return {
          from: today.toISOString(),
          to: now.toISOString()
        };
      
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const endOfYesterday = new Date(yesterday);
        endOfYesterday.setHours(23, 59, 59, 999);
        return {
          from: yesterday.toISOString(),
          to: endOfYesterday.toISOString()
        };
      
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return {
          from: startOfWeek.toISOString(),
          to: now.toISOString()
        };
      
      case 'lastWeek':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        lastWeekEnd.setHours(23, 59, 59, 999);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        lastWeekStart.setHours(0, 0, 0, 0);
        return {
          from: lastWeekStart.toISOString(),
          to: lastWeekEnd.toISOString()
        };
      
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          from: startOfMonth.toISOString(),
          to: now.toISOString()
        };
      
      case 'custom':
        // For custom, we'll need to store the custom dates used for the report
        // This is a simplified version - you might want to store these separately
        if (!this.reportCustomFromDate || !this.reportCustomToDate) {
          throw new Error('Custom date range not available');
        }
        return {
          from: new Date(this.reportCustomFromDate).toISOString(),
          to: new Date(this.reportCustomToDate).toISOString()
        };
      
      default:
        throw new Error('Invalid period');
    }
  }

  getReportDateRange(): { from: string; to: string } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (this.reportPeriod) {
      case 'today':
        return {
          from: today.toISOString(),
          to: now.toISOString()
        };
      
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const endOfYesterday = new Date(yesterday);
        endOfYesterday.setHours(23, 59, 59, 999);
        return {
          from: yesterday.toISOString(),
          to: endOfYesterday.toISOString()
        };
      
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return {
          from: startOfWeek.toISOString(),
          to: now.toISOString()
        };
      
      case 'lastWeek':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        lastWeekEnd.setHours(23, 59, 59, 999);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        lastWeekStart.setHours(0, 0, 0, 0);
        return {
          from: lastWeekStart.toISOString(),
          to: lastWeekEnd.toISOString()
        };
      
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          from: startOfMonth.toISOString(),
          to: now.toISOString()
        };
      
      case 'custom':
        if (!this.reportCustomFromDate || !this.reportCustomToDate) {
          throw new Error('Please select both from and to dates');
        }
        return {
          from: new Date(this.reportCustomFromDate).toISOString(),
          to: new Date(this.reportCustomToDate).toISOString()
        };
      
      default:
        throw new Error('Please select a valid period');
    }
  }

  getDateRange(): { from: string; to: string } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (this.replayPeriod) {
      case 'today':
        return {
          from: today.toISOString(),
          to: now.toISOString()
        };
      
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const endOfYesterday = new Date(yesterday);
        endOfYesterday.setHours(23, 59, 59, 999);
        return {
          from: yesterday.toISOString(),
          to: endOfYesterday.toISOString()
        };
      
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return {
          from: startOfWeek.toISOString(),
          to: now.toISOString()
        };
      
      case 'lastWeek':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        lastWeekEnd.setHours(23, 59, 59, 999);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        lastWeekStart.setHours(0, 0, 0, 0);
        return {
          from: lastWeekStart.toISOString(),
          to: lastWeekEnd.toISOString()
        };
      
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return {
          from: startOfMonth.toISOString(),
          to: now.toISOString()
        };
      
      case 'custom':
        if (!this.customFromDate || !this.customToDate) {
          throw new Error('Please select both from and to dates');
        }
        return {
          from: new Date(this.customFromDate).toISOString(),
          to: new Date(this.customToDate).toISOString()
        };
      
      default:
        throw new Error('Please select a valid period');
    }
  }

  async loadVehiclePosition() {
    try {
      this.loading = true;
      this.errorMessage = '';
      
      // console.log('Loading vehicle position for uniqueId:', this.traccarUniqueId);
      // console.log('Traccar token available:', !!env.VUE_APP_PUBLIC_TRACCAR_TOKEN);
      
      const position = await traccarService.getLatestPositionByUniqueId(this.traccarUniqueId);
      // console.log('Received position:', position);
      
      if (position) {
        this.addVehicleMarker(position);
        this.centerMapOnVehicle(position);
        this.extractCurrentVehicleData(position);
      } else {
        this.errorMessage = 'No position data found for vehicle';
      }
    } catch (error) {
      console.error('Error loading vehicle position:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        this.errorMessage = `API Error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`;
      } else if (error.request) {
        console.error('Request failed:', error.request);
        this.errorMessage = 'Network error - unable to reach Traccar server';
      } else {
        console.error('Error:', error.message);
        this.errorMessage = `Error: ${error.message}`;
      }
    } finally {
      this.loading = false;
    }
  }

  addVehicleMarker(position: TraccarPosition) {
    if (!this.map) {
      return;
    }

    // Remove existing marker if it exists
    if (this.vehicleMarker) {
      this.vehicleMarker.remove();
    }

    // Create a new marker for the vehicle
    this.vehicleMarker = new mapboxgl.Marker({
      color: '#FF0000', // Red color for the vehicle
      scale: 1.2
    })
      .setLngLat([position.longitude, position.latitude])
      .addTo(this.map);

    // Add popup with vehicle information
    const popup = new mapboxgl.Popup({ offset: 25 })
      .setHTML(`
        <div>
          <h6>Vehicle Location</h6>
          <p><strong>Speed:</strong> ${Math.round(position.speed * 1.852)} km/h</p>
          <p><strong>Last Update:</strong> ${new Date(position.fixTime).toLocaleString()}</p>
          ${position.address ? `<p><strong>Address:</strong> ${position.address}</p>` : ''}
        </div>
      `);

    this.vehicleMarker.setPopup(popup);
  }

  centerMapOnVehicle(position: TraccarPosition) {
    if (!this.map) {
      return;
    }

    // Center the map on the vehicle's position
    this.map.setCenter([position.longitude, position.latitude]);
  }

  extractCurrentVehicleData(position: TraccarPosition) {
    this.currentVehicleData = {
      motion: position.attributes?.motion || false,
      ignition: position.attributes?.ignition || false,
      batteryLevel: position.attributes?.batteryLevel || position.attributes?.battery || null
    };
  }

  getBatteryClass(batteryLevel: number): string {
    if (batteryLevel >= 75) {
      return 'badge badge-success';
    } else if (batteryLevel >= 50) {
      return 'badge badge-info';
    } else if (batteryLevel >= 25) {
      return 'badge badge-warning';
    } else {
      return 'badge badge-danger';
    }
  }

  formatEngineHours(hours: number): string {
    if (hours === null || hours === undefined || hours === 0) {
      return '0h 0m';
    }
    
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    return `${wholeHours}h ${minutes}m`;
  }

  formatBatteryLevel(batteryLevel: number): string {
    return batteryLevel.toFixed(1) + '%';
  }

  displayRouteOnMap(positions: TraccarPosition[]) {
    if (!this.map || positions.length === 0) {
      return;
    }

    // Clear existing route visualization
    this.clearRouteVisualization();

    // Sort positions by time to ensure correct route order
    const sortedPositions = positions.sort((a, b) => 
      new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime()
    );

    // Store positions for animation
    this.currentPositions = sortedPositions;

    // Create route line coordinates
    const routeCoordinates = sortedPositions.map(pos => [pos.longitude, pos.latitude]);

    // Add route line and animated car
    this.addAnimatedRouteToMap(routeCoordinates);

    // Fit map to show entire route
    this.fitMapToRoute(routeCoordinates);
  }

  addAnimatedRouteToMap(coordinates: number[][]) {
    if (!this.map) return;

    const origin = coordinates[0];

    // Route line data
    const route = {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: coordinates
          }
        }
      ]
    };

    // Animated point data (car)
    const point = {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'Point' as const,
            coordinates: origin
          }
        }
      ]
    };

    // Wait for map to load before adding sources and layers
    const addSourcesAndLayers = () => {
      if (!this.map) return;

      // Create a simple car icon using canvas with proper dimensions
      const createCarIcon = () => {
        const size = 32; // Use power of 2 size
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = size;
        canvas.height = size;
        
        if (ctx) {
          // Clear canvas
          ctx.clearRect(0, 0, size, size);
          
          // Draw a simple car shape centered in the canvas
          const centerX = size / 2;
          const centerY = size / 2;
          
          // Car body
          ctx.fillStyle = '#ff0000'; // Red car
          ctx.fillRect(centerX - 12, centerY - 3, 24, 6); // Car body
          ctx.fillRect(centerX - 8, centerY - 7, 16, 4); // Car roof
          
          // Car wheels
          ctx.fillStyle = '#333333';
          ctx.beginPath();
          ctx.arc(centerX - 7, centerY + 4, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(centerX + 7, centerY + 4, 2, 0, Math.PI * 2);
          ctx.fill();
          
          // Car outline
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1;
          ctx.strokeRect(centerX - 12, centerY - 3, 24, 6);
          ctx.strokeRect(centerX - 8, centerY - 7, 16, 4);
        }
        
        return canvas;
      };

      // Add the car icon to the map with proper format
      const carIcon = createCarIcon();
      const imageData = carIcon.getContext('2d')?.getImageData(0, 0, carIcon.width, carIcon.height);
      
      if (imageData) {
        this.map.addImage('car-icon', {
          width: carIcon.width,
          height: carIcon.height,
          data: imageData.data
        });
      }

      // Add route source and layer
      this.map.addSource('route', {
        type: 'geojson',
        data: route
      });

      this.map.addSource('point', {
        type: 'geojson',
        data: point
      });

      this.map.addLayer({
        id: 'route',
        source: 'route',
        type: 'line',
        paint: {
          'line-width': 2,
          'line-color': '#007cbf'
        }
      });

      this.map.addLayer({
        id: 'point',
        source: 'point',
        type: 'symbol',
        layout: {
          'icon-image': 'car-icon',
          'icon-size': 1.0,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          // 'icon-rotate': ['get', 'bearing'],
          // 'icon-rotation-alignment': 'map'
        }
      });

      // Start the animation
      this.startCarAnimation(coordinates, point);
    };

    if (this.map.loaded()) {
      addSourcesAndLayers();
    } else {
      this.map.on('load', addSourcesAndLayers);
    }
  }

  startCarAnimation(coordinates: number[][], point: any) {
    if (!this.map) return;

    let counter = 0;
    const steps = coordinates.length - 1;

    const animate = () => {
      if (!this.map || counter >= steps) {
        this.isAnimating = false;
        return;
      }

      this.isAnimating = true;

      // Get current and next coordinates
      const start = coordinates[counter];
      const end = coordinates[counter + 1];

      if (!end) {
        this.isAnimating = false;
        return;
      }

      // Calculate bearing for car rotation
      const bearing = this.calculateBearing(start, end);

      // Update point position and bearing
      point.features[0].geometry.coordinates = coordinates[counter];
      point.features[0].properties.bearing = bearing;

      // Update the map source
      const pointSource = this.map.getSource('point') as mapboxgl.GeoJSONSource;
      if (pointSource) {
        pointSource.setData(point);
      }

      counter++;

      // Continue animation
      this.animationId = requestAnimationFrame(animate);
    };

    // Start animation
    animate();
  }

  calculateBearing(start: number[], end: number[]): number {
    const startLat = start[1] * Math.PI / 180;
    const startLng = start[0] * Math.PI / 180;
    const endLat = end[1] * Math.PI / 180;
    const endLng = end[0] * Math.PI / 180;

    const dLng = endLng - startLng;

    const y = Math.sin(dLng) * Math.cos(endLat);
    const x = Math.cos(startLat) * Math.sin(endLat) - Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng);

    const bearing = Math.atan2(y, x) * 180 / Math.PI;
    return (bearing + 360) % 360;
  }

  fitMapToRoute(coordinates: number[][]) {
    if (!this.map || coordinates.length === 0) return;

    // Calculate bounds for all coordinates
    const bounds = coordinates.reduce((bounds, coord) => {
      return bounds.extend(coord as [number, number]);
    }, new mapboxgl.LngLatBounds(coordinates[0] as [number, number], coordinates[0] as [number, number]));

    // Fit map to show entire route with padding
    this.map.fitBounds(bounds, {
      padding: 50,
      maxZoom: 15 // Don't zoom in too much
    });
  }

  clearRouteVisualization() {
    if (!this.map) return;

    // Stop animation
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.isAnimating = false;

    // Remove all route markers
    this.routeMarkers.forEach(marker => marker.remove());
    this.routeMarkers = [];

    // Remove route layers and sources
    if (this.map.getLayer('route')) {
      this.map.removeLayer('route');
    }
    if (this.map.getLayer('point')) {
      this.map.removeLayer('point');
    }
    if (this.map.getSource('route')) {
      this.map.removeSource('route');
    }
    if (this.map.getSource('point')) {
      this.map.removeSource('point');
    }

    // Clear stored positions
    this.currentPositions = [];
  }

  replayCarAnimation() {
    if (!this.map || this.currentPositions.length === 0) return;

    // Clear existing animation
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    const coordinates = this.currentPositions.map(pos => [pos.longitude, pos.latitude]);
    const origin = coordinates[0];

    // Reset the car to the start position
    const point = {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: { bearing: 0 },
          geometry: {
            type: 'Point' as const,
            coordinates: origin
          }
        }
      ]
    };

    // Update the point source
    const pointSource = this.map.getSource('point') as mapboxgl.GeoJSONSource;
    if (pointSource) {
      pointSource.setData(point);
    }

    // Restart animation
    this.startCarAnimation(coordinates, point);
  }

  initMap() {
    this.map = new mapboxgl.Map({
      container: this.$el.querySelector('.map-box') as HTMLElement,
      style: "mapbox://styles/mapbox/light-v10",
      zoom: 13,
      center: [-112.8345, 49.6942] // Lethbridge, Alberta, Canada coordinates
    });

    this.map.addControl(new mapboxgl.NavigationControl());
    this.map.resize();
  }

  destroyMap() {
    if (!this.map) {
      return;
    }
    
    // Clear route visualization
    this.clearRouteVisualization();
    
    // Remove vehicle marker before destroying map
    if (this.vehicleMarker) {
      this.vehicleMarker.remove();
      this.vehicleMarker = undefined;
    }
    
    this.map.stop();
    this.map.off("load", () => this.map);
    this.map.remove();
    this.map = undefined;
  }
}
